import { ENVELOPE_RESERVED_KEYS } from "@/src/lib/tracking/reservedKeys";
import { AD_SOURCE_QUERY_KEY } from "./adSource";

/**
 * `ad_link_open` —— **拿到广告链接参数**这一统一动作的唯一事件（所有广告来源共用）。
 *
 * 与原生 `kmp-app/.../analytics/AdLinkOpenTracking.kt` 同名同规则：「拿到参数」对所有广告来源是
 * 同一件事（规范 §3.1 N2/N3），渠道差异进属性；进入各承接页之后才是各自独立的事件。
 *
 * 属性**不写死 key**：链接上带了什么业务参数就原样带什么（投放新加参数无需改代码）。
 * 透传规则：
 * 1. key 统一 snake_case（`inviteCode` → `invite_code`），非 `[a-z0-9_]` 字符折成 `_`
 * 2. `roast_card_id` → `roast_id`（与 roast 其它事件同键）
 * 3. 撞 envelope 保留键或本事件自有键（`resolve_via`）→ 加 `link_` 前缀（规范 §3.2：否则被静默覆盖）
 * 4. 能对应到具体人的 key（email / phone / invite）只记 `has_<key>=true`；凭据类 key 由 `Track` 统一脱敏。
 *    **值**里内嵌邀请码的（如 `adj_deep_link=edgen://edgen?inviteCode=…`、`/i/{code}`、`/task/invite/{code}`）同样只记 `has_<key>`
 * 5. 值 trim 后截断到 {@link AD_LINK_MAX_VALUE_LENGTH}；空值丢弃
 * 6. `adj_*` 保留（投放的 campaign / adgroup / creative 在这里）
 * 7. `ad_source` 统一大写；没带但有 `roast_id` 时补 `ROAST_HANDOFF`
 *    （web 入口 Proxy 只在已带 ad_source 时才处理，这条在 web 上不会命中 —— 保留是为了两端规则逐字一致）
 *
 * 本文件**零运行时依赖**（保留键取自 `reservedKeys`，不经 logbeacon），Proxy（服务端）直接调用：
 * 存进 cookie 的已是脱敏后的参数，不会把 email / phone 原值落进浏览器 cookie。
 */
export const AD_LINK_OPEN_EVENT = "ad_link_open";

/** 本事件自有键：参数从哪条路拿到（web 恒为 {@link AD_LINK_RESOLVE_VIA_PROXY}）。 */
export const AD_LINK_RESOLVE_VIA_KEY = "resolve_via";

/** web 入口：Proxy 捕获 `?ad_source=` 时暂存参数，客户端首帧上报。 */
export const AD_LINK_RESOLVE_VIA_PROXY = "proxy";

/** 单值上限：防止长 URL 类参数把整条事件顶到 oversize。 */
export const AD_LINK_MAX_VALUE_LENGTH = 200;

/**
 * Proxy → 客户端传递**已脱敏参数**（JSON）的一次性 cookie（读出即删）。
 * 为什么不在 Proxy 直接上报：Proxy 跑在服务端，OpenReplay 只在浏览器里有。
 */
export const AD_LINK_PARAMS_COOKIE_KEY = "edgen_ad_link_params";

/** cookie 存活时间：只需熬过「落地 → 可能的登录 307 → 客户端首帧」。 */
export const AD_LINK_PARAMS_COOKIE_MAX_AGE_SECONDS = 60 * 60;

/**
 * cookie 值**编码后**的长度上限。Next 写 cookie 时会再 `encodeURIComponent` 一次
 * （`@edge-runtime/cookies` stringifyCookie）。卡在 1KB 而不是逼近浏览器单 cookie 4KB：
 * 同一个 307 还带着 ad_source 等既有 Set-Cookie，响应头合计要远低于网关常见的 4KB 头缓冲，
 * 否则长链接会让广告落地本身 502。超长时丢的只是非核心参数。
 */
export const AD_LINK_COOKIE_MAX_ENCODED_LENGTH = 1024;

type AdLinkOpenParams = Record<string, string | boolean>;

const ROAST_CARD_ID_KEY = "roast_card_id";
const ROAST_ID_KEY = "roast_id";
const LANDING_ID_KEY = "landing_id";
const AD_SOURCE_ROAST_HANDOFF = "ROAST_HANDOFF";
const COLLISION_PREFIX = "link_";
const PRESENCE_PREFIX = "has_";
const PERSONAL_KEY_RE = /(email|phone|mobile|invite)/;
/** 值里内嵌邀请码：query 键（含二次编码）或邀请路径段。 */
const PERSONAL_VALUE_RE =
  /invite_?code(=|%3d)|\/i\/|%2fi%2f|\/task\/invite\/|%2ftask%2finvite%2f/i;

/** cookie 超长需要裁剪时永不丢的键（识别「是什么广告」的最小集）。 */
const CORE_KEYS: ReadonlySet<string> = new Set([
  AD_SOURCE_QUERY_KEY,
  LANDING_ID_KEY,
  ROAST_ID_KEY,
]);

function normalizeKey(raw: string): string | null {
  const snake = raw
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!snake) {
    return null;
  }
  if (snake === ROAST_CARD_ID_KEY) {
    return ROAST_ID_KEY;
  }
  if (ENVELOPE_RESERVED_KEYS.has(snake) || snake === AD_LINK_RESOLVE_VIA_KEY) {
    return COLLISION_PREFIX + snake;
  }
  return snake;
}

/**
 * 透传规则的纯函数实现；无任何参数时返回空对象，调用方不发事件。
 * 内部用 Map：普通对象的 `in` / 赋值会碰到原型链（`constructor`、`__proto__` 这类参数名会被吞掉）。
 */
export function buildAdLinkOpenParams(rawQuery: string): AdLinkOpenParams {
  const query = rawQuery.startsWith("?") ? rawQuery.slice(1) : rawQuery;
  const out = new Map<string, string | boolean>();
  for (const [rawKey, rawValue] of new URLSearchParams(query)) {
    const key = normalizeKey(rawKey);
    const value = rawValue.trim().slice(0, AD_LINK_MAX_VALUE_LENGTH);
    if (!key || !value || out.has(key)) {
      continue;
    }
    if (PERSONAL_KEY_RE.test(key) || PERSONAL_VALUE_RE.test(rawValue)) {
      out.set(PRESENCE_PREFIX + key, true);
      continue;
    }
    out.set(key, key === AD_SOURCE_QUERY_KEY ? value.toUpperCase() : value);
  }
  if (!out.has(AD_SOURCE_QUERY_KEY) && out.has(ROAST_ID_KEY)) {
    out.set(AD_SOURCE_QUERY_KEY, AD_SOURCE_ROAST_HANDOFF);
  }
  return Object.fromEntries(out);
}

const encodedLength = (value: string): number =>
  encodeURIComponent(value).length;

/**
 * Proxy 侧：参数 → cookie 值（JSON）。编码后超长时从后往前丢非核心键，
 * 核心键（ad_source / landing_id / roast_id）永远保留；无参数返回 null（不写 cookie）。
 */
export function serializeAdLinkParamsForCookie(
  params: AdLinkOpenParams,
): string | null {
  const entries = Object.entries(params);
  if (entries.length === 0) {
    return null;
  }
  let json = JSON.stringify(Object.fromEntries(entries));
  for (
    let i = entries.length - 1;
    i >= 0 && encodedLength(json) > AD_LINK_COOKIE_MAX_ENCODED_LENGTH;
    i--
  ) {
    if (CORE_KEYS.has(entries[i][0])) {
      continue;
    }
    entries.splice(i, 1);
    json = JSON.stringify(Object.fromEntries(entries));
  }
  return json;
}

/** 客户端侧：cookie 值 → 参数；格式不对（被篡改 / 旧格式）返回空对象，调用方不发事件。 */
export function parseAdLinkParamsCookie(raw: string): AdLinkOpenParams {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {};
  }
  const out = new Map<string, string | boolean>();
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value === "string" || typeof value === "boolean") {
      out.set(key, value);
    }
  }
  return Object.fromEntries(out);
}
